import { useState } from 'react'

import { ApiError, fehlertext } from '../../api/client'
import { useBuildBook } from '../../hooks/queries'
import { alsApp } from '../../lib/betriebsart'
import { groesse } from '../../lib/format'
import { notiere } from '../../lib/sendeprotokoll'
import { sichereDatei } from '../../lib/sichern'
import { mapImage } from './mapImage'
import { useTexte } from '../../texte'

/**
 * "Als PDF" and "Als EPUB" -- the server builds, the browser supplies the maps.
 *
 * Both buttons wait until the maps are drawn (`ready`), then collect their
 * still images and send them with the job. When the address comes back, the
 * file is saved the same way as a photo (`sichereDatei`): the share sheet on
 * iPhone and iPad, a download everywhere else. **And a link stays** under
 * the buttons -- from use: "der Download von PDF und epub funktioniert nur
 * im Browser, aber nicht in der WebApp. oder ein Link?" In the app started
 * from the home screen a `<a download>` does nothing on iOS; the link opens
 * the file in Safari, where it can be saved.
 *
 * **There is no button that does nothing:** while not ready, it says what
 * is being waited for; if the build fails, the reason is there as text.
 */
export function ExportButtons({
  tripId,
  withUser,
  from,
  to,
  ready,
  waitingText,
}: {
  tripId: number
  /** The person of the private document -- otherwise the book. */
  withUser?: number
  from: string | null
  to: string | null
  ready: boolean
  waitingText: string
}) {
  const t = useTexte().buch
  const build = useBuildBook(tripId)
  /*
   * **Which of the three steps is running** -- from use: "Meldung: wird
   * gebaut, aber auch nach einigen Minuten ist es nicht fertig." One word
   * for three steps said nothing about where it hung; now the button names
   * the step, and the trail under Settings › Diagnose keeps the times.
   */
  const [running, setRunning] = useState<{ format: 'pdf' | 'epub'; step: 'karten' | 'server' | 'holen' } | null>(null)
  const [built, setBuilt] = useState<{ url: string; filename: string; format: 'pdf' | 'epub' } | null>(null)

  async function maps(): Promise<{ route?: string; points: Record<string, string> }> {
    const route = document.querySelector<HTMLElement>('.buch__karte')
    const points: Record<string, string> = {}
    for (const box of Array.from(document.querySelectorAll<HTMLElement>('.buch__punktkarte[data-punkt]'))) {
      const id = box.dataset.punkt ?? ''
      const image = await mapImage(box)
      if (id !== '' && image !== null) points[id] = image
    }
    const routeImage = route === null ? null : await mapImage(route)
    return routeImage === null ? { points } : { route: routeImage, points }
  }

  async function start(format: 'pdf' | 'epub') {
    const started = performance.now()
    const since = () => ((performance.now() - started) / 1000).toFixed(1)
    setRunning({ format, step: 'karten' })
    try {
      const images = withUser === undefined ? await maps() : { points: {} }
      const size = Object.values(images.points).reduce((sum, i) => sum + i.length, images.route?.length ?? 0)
      notiere(`Buch ${format}: Karten abgemalt in ${since()} s (${Object.keys(images.points).length} Punkte, ${groesse(size)})`)
      setRunning({ format, step: 'server' })
      const result = await build.mutateAsync({
        format,
        with: withUser,
        from: from ?? '',
        to: to ?? '',
        maps: images,
      })
      notiere(`Buch ${format}: Server fertig nach ${since()} s (dort ${result.seconds} s, ${groesse(result.bytes)})`)
      const url = `${result.url}?name=${encodeURIComponent(result.filename)}`
      setBuilt({ url, filename: result.filename, format })
      setRunning({ format, step: 'holen' })
      try {
        const way = await sichereDatei(url, result.filename, format === 'pdf' ? 'application/pdf' : 'application/epub+zip')
        notiere(`Buch ${format}: Datei ${way} nach ${since()} s`)
      } catch (error) {
        // The link below stays: whoever cannot save here opens the file there.
        notiere(`Buch ${format}: Datei nicht gesichert nach ${since()} s: ${error instanceof Error ? error.message : String(error)}`)
      }
    } catch (error) {
      // The error is shown below as text -- `build.error`.
      notiere(`Buch ${format}: Fehler nach ${since()} s: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
      setRunning(null)
    }
  }

  const stepText = (step: 'karten' | 'server' | 'holen'): string =>
    step === 'karten' ? t.exportKarten : step === 'server' ? t.exportServer : t.exportHolen
  const errorText = (error: unknown): string =>
    error instanceof ApiError && error.timedOut ? t.exportKeineAntwort : fehlertext(error)

  return (
    <span className="exportknoepfe">
      {build.isError && (
        <span className="notice notice--knapp" role="alert">{errorText(build.error)}</span>
      )}
      {!ready && <span className="exportknoepfe__hinweis">{waitingText}</span>}
      {built !== null && (
        <span className="exportknoepfe__hinweis">
          <a href={built.url} target="_blank" rel="noopener" data-export="link">
            {t.dateiOeffnen(built.filename)}
          </a>
          {alsApp() && ` ${t.appHinweis}`}
        </span>
      )}
      <button
        type="button"
        className="button button--small"
        disabled={!ready || running !== null}
        data-export="pdf"
        onClick={() => void start('pdf')}
      >
        {running?.format === 'pdf' ? stepText(running.step) : t.alsPdf}
      </button>
      <button
        type="button"
        className="button button--small button--outline"
        disabled={!ready || running !== null}
        data-export="epub"
        onClick={() => void start('epub')}
      >
        {running?.format === 'epub' ? stepText(running.step) : t.alsEpub}
      </button>
    </span>
  )
}
